import React, { useEffect, useState } from 'react';
import { useLazyGetCalendarOrganizationsQuery } from '../../api/calendar.js';
import { useDebounce } from '../../hooks/useDebounce.js';
import BaseAutocomplete from '../form/BaseAutocomplete.jsx';
import BaseButton from '../form/BaseButton.jsx';
import IconAdd from '../images/IconAdd.jsx';
import BaseCard from './BaseCard.jsx';
import IconOrganize from '../images/IconOrganize.jsx';

const AssociateOrganizations = ({ fields, append, control, clearErrors, remove, showSearch, setShowSearch }) => {
    const [search, setSearch] = useState('');
    const [organizationList, setOrganizationList] = useState([]);
    const [getCalendarOrganizations, { isLoading: isLoadingOrganizations, isError: isErrorOrganizations }] =
        useLazyGetCalendarOrganizationsQuery();

    const debouncedSearch = useDebounce(search, 500);

    const selectOrganization = (id, name) => {
        append({
            organization_id: id,
            name: name
        });
        setSearch('');
    };

    const resetSearch = () => {
        setShowSearch(true);
        setOrganizationList([]);
        setSearch('');
    };

    useEffect(() => {
        (async () => {
            if (debouncedSearch !== '') {
                const result = await getCalendarOrganizations({ search: debouncedSearch });
                setOrganizationList(result.data.response.organizations);
            } else {
                setOrganizationList([]);
            }
        })();
    }, [debouncedSearch, getCalendarOrganizations]);

    return (
        <BaseCard className='border-0 bg-theme1-primary2 p-0 pb-6 mt-0 max-w-3xl w-full mx-auto'>
            <div className='max-w-3xl w-full mx-auto px-8 flex items-center justify-between p-6'>
                <div className='text-2xl font-bold text-theme1-white'>Organizations</div>
                <div onClick={() => resetSearch()}>
                    <IconAdd className='w-8 h-8 fill-theme1-white' />
                </div>
            </div>
            <BaseCard className='border-0 m-0 mb-6 p-4 pb-5 bg-theme1-gray'>
                <div className='max-w-3xl w-full mx-auto px-4'>
                    <div className='text-lg font-bold text-theme1-title2 pb-2'>Associate Organizations</div>

                    {showSearch && (
                        <div className='flex justify-between items-center'>
                            <div className='flex-grow'>
                                <BaseAutocomplete
                                    items={organizationList.map((organization) => ({
                                        label: organization.name,
                                        value: organization.id
                                    }))}
                                    onSelect={selectOrganization}
                                    name='search'
                                    value={search}
                                    placeholder='Search...'
                                    setValue={(value) => setSearch(value)}
                                    isLoading={isLoadingOrganizations}
                                    exclude={fields.map((item) => item.organization_id)}
                                />
                            </div>
                            <div className='text-theme1-primary2 cursor-pointer ml-4' onClick={() => resetSearch()}>
                                Reset
                            </div>
                        </div>
                    )}
                    {fields.length > 0 && (
                        <>
                            <hr className='my-4 border-theme1-disabled' />
                            {fields.map((organization, index) => {
                                return (
                                    <div
                                        key={organization.id}
                                        className='w-full flex items-center justify-between mt-4 bg-theme1-white rounded-lg p-4 border-theme1-cardStroke'
                                    >
                                        <div className='flex flex-row items-center justify-center'>
                                            <div className='mr-4'>
                                                <div className='rounded-full w-12 h-12 overflow-hidden'>
                                                    <div className='w-full h-full bg-theme1-pale flex items-center justify-center'>
                                                        <div className='text-theme1-primary font-bold text-xl'>
                                                            <div className='bg-theme1-gold p-5 rounded-2xl flex items-center justify-center'>
                                                                <IconOrganize className='w-7 h-7 stroke-theme1-white' />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div>
                                                <div className='text-theme1-primary font-bold text-lg'>
                                                    {organization.name}
                                                </div>
                                            </div>
                                        </div>
                                        <BaseButton
                                            className='ml-2 btn-red max-w-32 p-2'
                                            onClick={() => remove(index)}
                                            textValue='Remove'
                                        />
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>
            </BaseCard>
        </BaseCard>
    );
};

export default AssociateOrganizations;
